/**
 * MasteringDynamicEq — динамический эквалайзер мастеринга: прижимает частоту
 * только в те моменты, когда она выпирает из трека, и только на столько, на
 * сколько выпирает. Остальное время звук не трогается.
 *
 * Зачем (фидбек #23519, Премиум-3046, 01.10.2026): «нужен динамический
 * мастеринг или эквализация, чтобы автоматически подрезались частоты, которые
 * "выпирают" из трека. Мастеринг по всей длине трека такого эффекта не даёт».
 * Он прав по устройству: весь EQ цепочки статичен, а ResonanceTamer режет
 * только то, что звучит ≥ 80 % времени. За 11.09–01.10 он сработал 4 раза на
 * 940 авто-мастеров, и все четыре — ниже 300 Гц: на живой музыке выпирание
 * почти всегда приходит и уходит, а не стоит.
 *
 * ЧТО СЧИТАЕТСЯ «ВЫПИРАЕТ» — одна формула на «Авто» и «Вручную» (история
 * версий и замеры — docs/mastering-dynamic-eq.md):
 *   1) уровень полосы в 1/3 октавы, усреднённый за 60–200 мс (удары гаснут);
 *   2) минус сдвиг момента G — громче/тише, ушёл бас — это не выпирание;
 *   3) ПИКОВЫЙ путь: против «верха обычного» — медианы секундных максимумов
 *      полосы за ±4 с. Что бьёт каждую секунду (бочка, малый, хэт), — норма;
 *   4) УСТОЙЧИВЫЙ путь (только низ, где пиковый слеп из-за бочки): средняя за
 *      ±0.5 с против медианы таких средних за ±4 с — ловит бубнящую ноту;
 *   5) ворота от k·σ до (k+1)·σ над точкой отсчёта (σ — MAD полосы), срез
 *      возвращает к ней мягко (tanh) и не глубже «силы».
 *   Почему не «полоса против соседних полос» и не «против нормы всего трека» —
 *   замеры в разборе: первое режет ноты (срез на 92 % времени), второе путает
 *   аранжировка (а капелла «выпирает» над полным миксом).
 *
 * Карта срезов сглаживается по частоте («ширина») и по времени: упреждение
 * («атака») ставит срез РАНЬШЕ всплеска — офлайн мы знаем будущее, — а
 * отпускание не даёт усилению дрожать.
 *
 * ОБРАБОТКА — STFT: окно sqrt-Ханна с обеих сторон, перекрытие 75 % (сумма
 * Ханна = 2 → точная реконструкция). Пара каналов идёт ОДНИМ комплексным FFT
 * (L + iR): усиление вещественное и симметричное, поэтому обратное
 * преобразование возвращает отфильтрованные L и R в Re и Im. Одно и то же
 * усиление на все каналы — стереокартина не плывёт. Кадр без среза идёт мимо
 * FFT тем же сложением с окном — звук бит-в-бит тот же, что дал бы FFT-путь.
 *
 * ПАМЯТЬ — главное ограничение мастеринга (89 % падений по памяти — машины
 * 2 ГБ на обычных треках, см. reference_mastering_memory_architecture): модуль
 * работает ВНУТРИ переданного буфера. Кадр j пишет только сэмплы позади точки,
 * с которой читает кадр j+1, поэтому копии во весь трек нет. Рядом живут лишь
 * карта «кадры × полосы» (4 мин ≈ 2.5 МБ) и кольца по N сэмплов.
 *
 * API:
 *   resolve({mode:'off'|'auto'|'manual', depth, sens, speed, width, lo, hi})
 *       → внутренние параметры или null (выключено)
 *   process(buffer, params)            — синхронно (node-тесты)
 *   processAsync(buffer, params, {signal, onProgress}) — нарезанно, главный поток
 *   AUTO, FOCUS, LIMITS, describe(params) — для страницы
 * buffer — AudioBuffer или {numberOfChannels, sampleRate, length, getChannelData}.
 */
(function (root) {
    'use strict';

    // Ползунки страницы и «Авто» говорят на одном языке — этих чисел.
    var AUTO = { depth: 6, sens: 50, speed: 50, width: 50, lo: 2000, hi: 12000 };
    var LIMITS = {
        depth: [1, 12], sens: [0, 100], speed: [0, 100], width: [0, 100],
        lo: [30, 16000], hi: [150, 20000]
    };
    // «Что сглаживать» — это только ДИАПАЗОН. Скорость и ширина — отдельные
    // ручки «как»: пресет, который молча переписывает соседние ползунки,
    // путает (поставил скорость — нажал «Шипящие» — скорость уехала).
    var FOCUS = {
        harsh:     { lo: 2000, hi: 6000 },
        sibilance: { lo: 5000, hi: 11000 },
        boom:      { lo: 60,   hi: 300 },
        wide:      { lo: 40,   hi: 16000 }
    };

    var BPO = 3;                     // полос на октаву: 1/3 октавы (см. шапку — 1/6 ловила ноты)
    var MIN_ACTIVE_FRAMES = 40;      // меньше — статистика полосы ничего не значит
    var ACTIVE_FLOOR_DB = 35;        // кадр тише громкой части на 35 дБ — тишина/хвост
    var AUDIBLE_DB = 60;             // полоса на 60 дБ ниже самой громкой в кадре — не слышна
    var SIGMA_FLOOR_DB = 1.5;        // полоса, которая почти не гуляет, не срабатывает от 1 дБ
    var QG = 0.7;                    // процентиль сдвига момента (см. шапку)
    var G_LO = 150, G_HI = 12000;    // полосы, по которым меряется сдвиг момента
    var UNITY_DB = 0.01;             // кадр без среза идёт мимо FFT
    var DB2LN = Math.LN10 / 20;

    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function num(v, d) { v = +v; return isFinite(v) ? v : d; }

    /* Пользовательские настройки → внутренние числа. Одно место на страницу,
       «Авто» и тесты.
       - sens → k: ворота открываются от k·σ до (k+1)·σ НАД ВЕРХОМ ОБЫЧНОГО
         (0 → 2.75σ, 50 → 1.5σ, 100 → 0.25σ); пойманный выброс возвращается к
         верху обычного. Середина подобрана по 34 ИИ-трекам (02.10, «Авто»
         2–12 кГц): срез ≥1 дБ на ~8 % времени, ≥3 дБ — на ~3 %, ~14 мест в
         минуту, средний срез 2.75 дБ;
       - depth → потолок среза и крутизна (ratio ≤ 1: ниже линии не режем
         никогда);
       - speed → атака/отпускание экспоненциально: 40/500 мс … 3/40 мс;
       - width → размытие карты по частоте, σ в полосах 1/3 октавы. */
    function resolve(s) {
        s = s || {};
        var mode = s.mode === 'manual' ? 'manual' : (s.mode === 'auto' ? 'auto' : 'off');
        if (mode === 'off') return null;
        var src = mode === 'auto' ? AUTO : s;
        var depth = clamp(num(src.depth, AUTO.depth), LIMITS.depth[0], LIMITS.depth[1]);
        var sens = clamp(num(src.sens, AUTO.sens), 0, 100);
        var speed = clamp(num(src.speed, AUTO.speed), 0, 100);
        var width = clamp(num(src.width, AUTO.width), 0, 100);
        var lo = clamp(num(src.lo, AUTO.lo), LIMITS.lo[0], LIMITS.lo[1]);
        var hi = clamp(num(src.hi, AUTO.hi), LIMITS.hi[0], LIMITS.hi[1]);
        if (hi < lo * 1.26) hi = Math.min(LIMITS.hi[1], lo * 1.26);   // не уже трети октавы
        if (lo > hi / 1.26) lo = hi / 1.26;
        return {
            mode: mode, depth: depth, sens: sens, speed: speed, width: width, lo: lo, hi: hi,
            k: sens <= 50 ? 2.75 - 1.25 * (sens / 50) : 1.5 - 1.25 * ((sens - 50) / 50),
            ratio: Math.min(1, 0.65 + depth / 20),
            atkMs: 40 * Math.pow(3 / 40, speed / 100),
            relMs: 500 * Math.pow(40 / 500, speed / 100),
            // Детектор смотрит на уровни, усреднённые за avgMs: удары (20–80 мс)
            // гаснут, а резкая нота, «с» и бубнящая нота (≥ 100 мс) остаются.
            avgMs: 60 + 140 * (1 - speed / 100),
            sigma: 0.35 * Math.pow(1.8 / 0.35, width / 100)
        };
    }

    /* Человеческие величины тех же ручек — страница пишет их рядом с ползунками,
       тест сверяет, что подпись и обработка не разошлись. */
    function describe(P) {
        if (!P) return null;
        return {
            k: P.k, atkMs: P.atkMs, relMs: P.relMs,
            widthOct: (2 * P.sigma + 1) / BPO          // эффективная ширина среза, октав
        };
    }

    /* ── FFT: радикс-2 на месте ─────────────────────────────────────────────
       Таблицы поворотов лежат ПОДРЯД для каждой стадии (а не одной таблицей с
       шагом), первые две стадии — без умножений, перестановка — готовым
       списком пар. Замер 02.10 (node, N=2048): наивная форма — 126 мкс на одно
       преобразование, эта — 62. */
    var fftCache = {};
    function makeFFT(N) {
        if (fftCache[N]) return fftCache[N];
        var bits = Math.round(Math.log(N) / Math.LN2), i, j;
        var sw = [];
        for (i = 0; i < N; i++) {
            var r = 0, x = i;
            for (j = 0; j < bits; j++) { r = (r << 1) | (x & 1); x >>= 1; }
            if (r > i) sw.push(i, r);
        }
        var swaps = new Uint32Array(sw);
        var total = 0;
        for (var s0 = 8; s0 <= N; s0 <<= 1) total += s0 >> 1;
        var TR = new Float64Array(Math.max(1, total)), TI = new Float64Array(Math.max(1, total));
        var o = 0;
        for (var s1 = 8; s1 <= N; s1 <<= 1) {
            var h1 = s1 >> 1;
            for (var k1 = 0; k1 < h1; k1++) { TR[o + k1] = Math.cos(2 * Math.PI * k1 / s1); TI[o + k1] = Math.sin(2 * Math.PI * k1 / s1); }
            o += h1;
        }
        function transform(re, im, inverse) {
            var a, b, t, k, xr, xi, ar, ai, n2 = swaps.length;
            for (var q = 0; q < n2; q += 2) {
                a = swaps[q]; b = swaps[q + 1];
                t = re[a]; re[a] = re[b]; re[b] = t; t = im[a]; im[a] = im[b]; im[b] = t;
            }
            for (a = 0; a < N; a += 2) {                       // size 2
                b = a + 1;
                xr = re[b]; xi = im[b];
                re[b] = re[a] - xr; im[b] = im[a] - xi; re[a] += xr; im[a] += xi;
            }
            if (N >= 4) {
                for (a = 0; a < N; a += 4) {                   // size 4: повороты 1 и ∓i
                    xr = re[a + 2]; xi = im[a + 2];
                    re[a + 2] = re[a] - xr; im[a + 2] = im[a] - xi; re[a] += xr; im[a] += xi;
                    ar = re[a + 3]; ai = im[a + 3];
                    if (inverse) { xr = -ai; xi = ar; } else { xr = ai; xi = -ar; }
                    re[a + 3] = re[a + 1] - xr; im[a + 3] = im[a + 1] - xi; re[a + 1] += xr; im[a + 1] += xi;
                }
            }
            var off = 0, sgn = inverse ? 1 : -1;
            for (var size = 8; size <= N; size <<= 1) {
                var half = size >> 1;
                for (var start = 0; start < N; start += size) {
                    for (k = 0; k < half; k++) {
                        var wr = TR[off + k], wi = sgn * TI[off + k];
                        a = start + k; b = a + half;
                        ar = re[b]; ai = im[b];
                        xr = ar * wr - ai * wi; xi = ar * wi + ai * wr;
                        re[b] = re[a] - xr; im[b] = im[a] - xi;
                        re[a] += xr; im[a] += xi;
                    }
                }
                off += half;
            }
        }
        var api = {
            fwd: function (re, im) { transform(re, im, false); },
            inv: function (re, im) { transform(re, im, true); }     // без 1/N — делим сами
        };
        fftCache[N] = api;
        return api;
    }

    // Окно кадра по частоте дискретизации; бас (нижняя граница ниже 150 Гц)
    // требует вдвое лучшего разрешения по частоте — иначе вся «бубнящая»
    // зона 60–300 Гц укладывается в две-три полосы.
    function frameSize(sr, lo) {
        var n = sr <= 50000 ? 2048 : (sr <= 100000 ? 4096 : 8192);
        if (lo < 150) n *= 2;
        return n;
    }

    /* Полосы по 1/3 октавы от 25 Гц до min(20 кГц, 0.92 Найквиста), каждая не
       уже двух бинов — внизу узкие полосы склеиваются, иначе уровень полосы из
       одного бина прыгает на ±5 дБ от кадра к кадру и «выпирает» шумом. */
    function bandLayout(N, sr) {
        var binHz = sr / N, half = N >> 1;
        var fTop = Math.min(20000, sr * 0.46);
        var edges = [], f;
        for (f = 25; f < fTop; f *= Math.pow(2, 1 / BPO)) edges.push(f);
        edges.push(fTop);
        var bands = [];
        var k0 = Math.max(1, Math.ceil(edges[0] / binHz)), f0 = edges[0];
        for (var i = 1; i < edges.length; i++) {
            var k1 = Math.min(half, Math.ceil(edges[i] / binHz));
            var last = i === edges.length - 1;
            if (k1 - k0 >= 2 || (last && k1 > k0)) {
                bands.push({ k0: k0, k1: k1, f0: f0, f1: edges[i] });
                k0 = k1; f0 = edges[i];
            }
        }
        var B = bands.length;
        var fc = new Float64Array(B), bandOf = new Int16Array(half + 1), cnt = new Float32Array(B);
        for (i = 0; i <= half; i++) bandOf[i] = -1;
        for (i = 0; i < B; i++) {
            fc[i] = Math.sqrt(bands[i].f0 * bands[i].f1);
            cnt[i] = bands[i].k1 - bands[i].k0;
            for (var k = bands[i].k0; k < bands[i].k1; k++) bandOf[k] = i;
        }
        // Интерполяция среза полос в бины — по лог-частоте между центрами.
        var bi = new Int16Array(half + 1), ba = new Float32Array(half + 1);
        for (k = 0; k <= half; k++) {
            var fk = k * binHz;
            if (B < 2 || fk <= fc[0]) { bi[k] = 0; ba[k] = 0; continue; }
            if (fk >= fc[B - 1]) { bi[k] = B - 2; ba[k] = 1; continue; }
            var lo = 0, hi = B - 1;
            while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (fc[mid] <= fk) lo = mid; else hi = mid; }
            bi[k] = lo; ba[k] = Math.log(fk / fc[lo]) / Math.log(fc[lo + 1] / fc[lo]);
        }
        return { B: B, fc: fc, cnt: cnt, bandOf: bandOf, bi: bi, ba: ba, binHz: binHz };
    }

    // Вес диапазона: 1 внутри [lo, hi], за краями — спад по косинусу на треть октавы.
    function rangeWeight(f, lo, hi) {
        var t = 1 / 3;
        if (f >= lo && f <= hi) return 1;
        var oct = f < lo ? Math.log(lo / f) / Math.LN2 : Math.log(f / hi) / Math.LN2;
        if (oct >= t) return 0;
        return 0.5 + 0.5 * Math.cos(Math.PI * oct / t);
    }

    function abortErr() { var e = new Error('MasteringDynamicEq aborted'); e.name = 'AbortError'; return e; }

    // Квантиль по гистограмме (шаг HS) — медианы и MAD по десяткам тысяч кадров
    // без сортировки.
    function histQuantile(h, base, nb, count, q, lo, step) {
        var need = q * count, acc = 0;
        for (var i = 0; i < nb; i++) { acc += h[base + i]; if (acc >= need) return lo + i * step; }
        return lo + (nb - 1) * step;
    }

    /* ── Задание: проходы, которые синхронный и нарезанный драйверы гоняют
       одинаково — поэтому node-тест проверяет ТОТ ЖЕ звук, что уходит в мастер. */
    function createJob(buffer, P) {
        var nCh = buffer.numberOfChannels, sr = buffer.sampleRate, len = buffer.length;
        var chans = [];
        for (var c = 0; c < nCh; c++) chans.push(buffer.getChannelData(c));
        var N = frameSize(sr, P.lo), hop = N >> 2, half = N >> 1;
        var lay = bandLayout(N, sr), B = lay.B;
        var off = N - hop;
        var J = len > 0 ? Math.floor((len - 1 + off) / hop) + 1 : 0;
        /* Анализ идёт вдвое реже обработки: кадр анализа ja = кадр обработки
           2·ja. Детектор всё равно смотрит на уровни, усреднённые за ≥ 60 мс,
           а прямой FFT анализа — самый дорогой проход (замер 02.10). */
        var hopA = hop * 2, JA = J > 0 ? ((J + 1) >> 1) : 0;
        var fft = makeFFT(N);
        var win = new Float64Array(N), winA = new Float64Array(N), wprod = new Float64Array(N), wsyn = new Float64Array(N);
        for (var n = 0; n < N; n++) {
            win[n] = Math.sin(Math.PI * n / N);
            winA[n] = win[n] * win[n];                 // анализ — полный Ханн: утечка ниже
            wprod[n] = win[n] * win[n] * 0.5;           // анализ × синтез × нормировка OLA (сумма Ханна = 2)
            wsyn[n] = win[n] * 0.5 / N;                 // синтез × нормировка × 1/N обратного FFT
        }
        var re = new Float64Array(N), im = new Float64Array(N);
        var pairs = Math.ceil(nCh / 2);
        var M = new Float32Array(Math.max(1, JA * B));  // мощность полос → срез (дБ), по кадрам анализа
        var lev = new Float32Array(Math.max(1, JA));     // мощность кадра анализа
        var rangeW = new Float32Array(B), inG = new Uint8Array(B);
        for (var b = 0; b < B; b++) {
            rangeW[b] = rangeWeight(lay.fc[b], P.lo, P.hi);
            inG[b] = lay.fc[b] >= G_LO && lay.fc[b] <= G_HI ? 1 : 0;
        }
        var levLo = Math.max(1, Math.ceil(40 / lay.binHz)), levHi = Math.min(half, Math.floor(16000 / lay.binHz));

        function loadPair(j, p, win) {
            var s = j * hop - off, a = chans[2 * p], bch = (2 * p + 1 < nCh) ? chans[2 * p + 1] : null, n2;
            if (s >= 0 && s + N <= len) {                // внутри трека — без проверок на сэмпл
                if (bch) for (n2 = 0; n2 < N; n2++) { re[n2] = a[s + n2] * win[n2]; im[n2] = bch[s + n2] * win[n2]; }
                else for (n2 = 0; n2 < N; n2++) { re[n2] = a[s + n2] * win[n2]; im[n2] = 0; }
                return;
            }
            for (n2 = 0; n2 < N; n2++) {
                var pos = s + n2, w = win[n2];
                if (pos >= 0 && pos < len) { re[n2] = a[pos] * w; im[n2] = bch ? bch[pos] * w : 0; }
                else { re[n2] = 0; im[n2] = 0; }
            }
        }

        // Проход 1: мощность полос и кадра (по всем каналам).
        function pass1(j0, j1) {
            var bandOf = lay.bandOf;
            for (var j = j0; j < j1; j++) {
                var rowOff = j * B, fl = 0;
                for (var b2 = 0; b2 < B; b2++) M[rowOff + b2] = 0;
                for (var p = 0; p < pairs; p++) {
                    loadPair(2 * j, p, winA);
                    fft.fwd(re, im);
                    for (var k = 0; k <= half; k++) {
                        var kk = k === 0 ? 0 : N - k;
                        var pw = 0.5 * (re[k] * re[k] + im[k] * im[k] + re[kk] * re[kk] + im[kk] * im[kk]);
                        var bb = bandOf[k];
                        if (bb >= 0) M[rowOff + bb] += pw;
                        if (k >= levLo && k <= levHi) fl += pw;
                    }
                }
                lev[j] = fl;
            }
        }

        // ── Обнаружение ──
        var active = new Uint8Array(Math.max(1, JA)), nActive = 0;
        var fmax = new Float32Array(Math.max(1, JA));    // самая громкая полоса кадра, дБ
        var G = new Float32Array(Math.max(1, JA));       // сдвиг момента, дБ
        var Lt = new Float32Array(B), SD = new Float32Array(B), SDs = new Float32Array(B), ok = new Uint8Array(B);
        var S2 = null;                                   // остаток УСТОЙЧИВОГО уровня (см. sustain())
        var HL = -200, HS = 0.1, HNB = Math.round(260 / HS) + 1;   // −200 … +60 дБ
        var hist = null, hcnt = new Uint32Array(B);
        var prevRow = new Float32Array(B), curRow = new Float32Array(B), tmp = new Float32Array(B);
        var gv = new Float32Array(B);
        var SENTINEL = -1000;

        function prepActive() {
            // Громкая часть трека — 95-й процентиль мощности кадров.
            var vals = [];
            for (var j = 0; j < JA; j++) if (lev[j] > 1e-20) vals.push(10 * Math.log(lev[j]) / Math.LN10);
            if (!vals.length) return;
            vals.sort(function (x, y) { return x - y; });
            var loud = vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.95))];
            var floor = Math.pow(10, (loud - ACTIVE_FLOOR_DB) / 10);
            for (j = 0; j < JA; j++) { active[j] = lev[j] >= floor && lev[j] > 1e-20 ? 1 : 0; nActive += active[j]; }
        }

        /* Шаг A: уровень полосы, дБ на бин, усреднённый в мощности треугольником
           шириной avgMs. Пишется в L2 (мощности в M ещё читают соседние кадры). */
        var L2 = new Float32Array(Math.max(1, JA * B));   // уровень → остаток против локальной нормы
        var hA = Math.max(1, Math.round((P.avgMs / 1000) * sr / hopA / 2));
        function detectA(j0, j1) {
            for (var j = j0; j < j1; j++) {
                var o = j * B, mx = -Infinity, b3, wsum = 0;
                for (b3 = 0; b3 < B; b3++) tmp[b3] = 0;
                for (var d = -hA; d <= hA; d++) {
                    var jj = j + d;
                    if (jj < 0 || jj >= JA) continue;
                    var w = hA + 1 - (d < 0 ? -d : d), oo = jj * B;
                    wsum += w;
                    for (b3 = 0; b3 < B; b3++) tmp[b3] += w * M[oo + b3];
                }
                for (b3 = 0; b3 < B; b3++) {
                    var dB = 10 * Math.log(tmp[b3] / wsum / lay.cnt[b3] + 1e-30) / Math.LN10;
                    L2[o + b3] = dB;
                    if (dB > mx) mx = dB;
                }
                fmax[j] = mx;
            }
        }

        // Медиана по звучащим кадрам от (L − сдвиг) для каждой полосы.
        function statLevels(useG) {
            hist = hist || new Uint32Array(B * HNB);
            hist.fill(0); hcnt.fill(0);
            for (var j = 0; j < JA; j++) {
                if (!active[j]) continue;
                var o = j * B, g = useG ? G[j] : 0;
                for (var b4 = 0; b4 < B; b4++) {
                    var v = L2[o + b4];
                    if (v < fmax[j] - AUDIBLE_DB) continue;
                    var hb = Math.round((clamp(v - g, HL, HL + 260) - HL) / HS);
                    hist[b4 * HNB + hb]++; hcnt[b4]++;
                }
            }
            for (b4 = 0; b4 < B; b4++) {
                ok[b4] = hcnt[b4] >= MIN_ACTIVE_FRAMES ? 1 : 0;
                Lt[b4] = ok[b4] ? histQuantile(hist, b4 * HNB, HNB, hcnt[b4], 0.5, HL, HS) : 0;
            }
        }

        // Сдвиг момента: верхний процентиль QG по полосам G_LO…G_HI от (L − Lтип).
        function statOffsets() {
            for (var j = 0; j < JA; j++) {
                if (!active[j]) { G[j] = 0; continue; }
                var o = j * B, m = 0;
                for (var b5 = 0; b5 < B; b5++) {
                    if (!inG[b5] || !ok[b5]) continue;
                    var v = L2[o + b5] - Lt[b5], z = m++;
                    while (z > 0 && gv[z - 1] > v) { gv[z] = gv[z - 1]; z--; }
                    gv[z] = v;
                }
                G[j] = m ? gv[Math.min(m - 1, Math.floor(QG * (m - 1) + 0.5))] : 0;
            }
        }

        /* ЛОКАЛЬНАЯ НОРМА. Сравнивать полосу с её нормой по всему треку нельзя:
           норму путает аранжировка. Замер 02.10 на стенде: в а капелле и во
           вступлении голос «выпирает» над нормой полного микса, медиана полосы
           3.6 кГц уезжает вверх, и резкая нота (+7 дБ к соседним нотам)
           оказывается всего на 1σ над ней — ворота её не видят. Норма здесь —
           медиана (L − G) этой полосы за ±LOCAL_S секунд, посчитанная по блокам
           0.25 с: выпирание = отличие от того, что полоса делала в этом же
           куске песни. Смена куплета на припев — это смена нормы, а не всплеск;
           резкость, которая длится весь припев, — характер припева, и её
           правит статический тон, а не этот модуль. */
        /* Две локальные нормы:
           — медиана блоков 0.25 с за ±LOCAL_S — для РАЗБРОСА полосы σ (MAD);
           — ВЕРХ ОБЫЧНОГО — медиана секундных МАКСИМУМОВ за ±LOCAL_S — точка
             отсчёта выпирания. То, что случается в каждой секунде (бочка, малый,
             хэт), задаёт этот верх и не режется; резкая нота или редкое «с»
             поднимают максимум одной-двух секунд из девяти и остаются выше.
           Почему не процентиль кадров: малый бьёт раз в секунду и занимает ~10 %
           кадров — выше 80-го процентиля; стенд 02.10 резал КАЖДЫЙ удар на 2–4 дБ
           на 7 кГц (трасса кадров). Высокая чувствительность (sens > 50) плавно
           опускает точку отсчёта к медиане — тогда прижимается и повторяющееся
           (частые «с» на «Шипящих»), как у классического de-esser'а. */
        var LOCAL_S = 4, BLOCK_S = 0.25, TOPBLOCK_S = 1, TOP_Q = 0.5;
        function localize() {
            var bl = Math.max(4, Math.round(BLOCK_S * sr / hopA)), nb = Math.ceil(JA / bl);
            var blT = Math.max(bl, Math.round(TOPBLOCK_S * sr / hopA)), nT = Math.ceil(JA / blT);
            var Qm = new Float32Array(Math.max(1, nb * B)), Rm = new Float32Array(Math.max(1, nb * B));
            var Qt = new Float32Array(Math.max(1, nT * B)), Rt = new Float32Array(Math.max(1, nT * B));
            var vals = new Float32Array(Math.max(bl, 2 * Math.round(LOCAL_S / BLOCK_S) + 1));
            var j, b6, i, m;
            function sortVals(n) {
                for (var x = 1; x < n; x++) { var v = vals[x], y = x; while (y > 0 && vals[y - 1] > v) { vals[y] = vals[y - 1]; y--; } vals[y] = v; }
            }
            function med(n) { return (n & 1) ? vals[n >> 1] : 0.5 * (vals[(n >> 1) - 1] + vals[n >> 1]); }
            // (L − G) на месте; тишина, неслышимые полосы и мелкие полосы — метка
            for (j = 0; j < JA; j++) {
                var o = j * B;
                for (b6 = 0; b6 < B; b6++) {
                    var v0 = L2[o + b6];
                    L2[o + b6] = (active[j] && ok[b6] && v0 >= fmax[j] - AUDIBLE_DB) ? v0 - G[j] : SENTINEL;
                }
            }
            for (i = 0; i < nb; i++) for (b6 = 0; b6 < B; b6++) {
                m = 0;
                for (j = i * bl; j < Math.min(JA, (i + 1) * bl); j++) { var v1 = L2[j * B + b6]; if (v1 > SENTINEL + 1) vals[m++] = v1; }
                if (m) { sortVals(m); Qm[i * B + b6] = med(m); } else Qm[i * B + b6] = NaN;
            }
            for (i = 0; i < nT; i++) for (b6 = 0; b6 < B; b6++) {
                var mx = -Infinity;
                for (j = i * blT; j < Math.min(JA, (i + 1) * blT); j++) { var v2 = L2[j * B + b6]; if (v2 > SENTINEL + 1 && v2 > mx) mx = v2; }
                Qt[i * B + b6] = mx > -Infinity ? mx : NaN;
            }
            function localMedian(Q, R, n, w, qq) {
                for (var i3 = 0; i3 < n; i3++) for (var b7 = 0; b7 < B; b7++) {
                    var m3 = 0;
                    for (var i4 = Math.max(0, i3 - w); i4 <= Math.min(n - 1, i3 + w); i4++) { var q = Q[i4 * B + b7]; if (q === q) vals[m3++] = q; }
                    if (!m3) { R[i3 * B + b7] = NaN; continue; }
                    sortVals(m3);
                    R[i3 * B + b7] = qq == null ? med(m3) : vals[Math.min(m3 - 1, Math.floor(qq * (m3 - 1) + 0.5))];
                }
            }
            localMedian(Qm, Rm, nb, Math.round(LOCAL_S / BLOCK_S));
            localMedian(Qt, Rt, nT, Math.round(LOCAL_S / TOPBLOCK_S), TOP_Q);
            function interp(R, n, size, jj, b) {
                var pos = (jj - size / 2) / size, i0 = Math.max(0, Math.min(n - 1, Math.floor(pos))), i1 = Math.min(n - 1, i0 + 1);
                var fr = Math.max(0, Math.min(1, pos - i0)), r0 = R[i0 * B + b], r1 = R[i1 * B + b];
                return r0 === r0 ? (r1 === r1 ? r0 + (r1 - r0) * fr : r0) : r1;
            }
            // точка отсчёта: верх обычного, при sens > 50 — плавно к медиане
            var wTop = P.sens > 50 ? 1 - (P.sens - 50) / 50 : 1;
            var DS = 0.05, DN = Math.round(60 / DS) + 1;
            var h2 = new Uint32Array(B * DN), c2 = new Uint32Array(B);
            for (j = 0; j < JA; j++) {
                for (b6 = 0; b6 < B; b6++) {
                    var e = L2[j * B + b6];
                    if (e <= SENTINEL + 1) continue;
                    var rm = interp(Rm, nb, bl, j, b6);
                    if (rm !== rm) continue;
                    h2[b6 * DN + Math.round(Math.min(Math.abs(e - rm), 60) / DS)]++; c2[b6]++;
                }
            }
            for (b6 = 0; b6 < B; b6++) {
                SD[b6] = c2[b6] >= MIN_ACTIVE_FRAMES ? Math.max(SIGMA_FLOOR_DB, 1.4826 * histQuantile(h2, b6 * DN, DN, c2[b6], 0.5, 0, DS)) : Infinity;
            }
            for (j = 0; j < JA; j++) {
                for (b6 = 0; b6 < B; b6++) {
                    var e2 = L2[j * B + b6];
                    if (e2 <= SENTINEL + 1) continue;
                    var rm2 = interp(Rm, nb, bl, j, b6), rt = interp(Rt, nT, blT, j, b6);
                    var ref = rt === rt ? (rm2 === rm2 ? rm2 + wTop * (rt - rm2) : rt) : rm2;
                    L2[j * B + b6] = ref === ref ? e2 - ref : SENTINEL;
                }
            }
            hist = null;
            sustain(Rm, nb, bl, interp, localMedian, vals, sortVals, med);
        }

        /* Путь УСТОЙЧИВОГО уровня. Пиковый путь (выше) меряет кадр против
           секундных максимумов — и не видит бубнения: в басу каждый такт бьёт
           бочка, максимумы секунд — её удары, а бубнящая нота гудит МЕЖДУ ними.
           Стенд 02.10: нота +7 дБ к средней мощности 80–125 Гц, а кадровый
           уровень на 89 Гц почти не сдвинулся (49–50 против 47–49 дБ рядом).
           Здесь — средняя мощность за ±SUST_S против медианы таких же средних за
           ±LOCAL_S: тянущееся выпирание (бубнящая нота, резкость на всю ноту)
           выпирает и тут, а удары растворяются в среднем. Срез — больший из двух. */
        var SUST_S = 0.5;
        function sustain(RmUnused, nb, bl, interp, localMedian, vals, sortVals, med) {
            var hs = Math.max(1, Math.round(SUST_S * sr / hopA));
            S2 = new Float32Array(Math.max(1, JA * B));
            var pre = new Float64Array(JA + 1), gpre = new Float64Array(JA + 1), apre = new Float64Array(JA + 1);
            var j, b8;
            for (j = 0; j < JA; j++) { gpre[j + 1] = gpre[j] + (active[j] ? G[j] : 0); apre[j + 1] = apre[j] + active[j]; }
            for (b8 = 0; b8 < B; b8++) {
                for (j = 0; j < JA; j++) pre[j + 1] = pre[j] + M[j * B + b8];
                for (j = 0; j < JA; j++) {
                    var o = j * B + b8;
                    if (!active[j] || !ok[b8] || L2[o] <= SENTINEL + 1) { S2[o] = SENTINEL; continue; }
                    var a = Math.max(0, j - hs), z = Math.min(JA, j + hs + 1);
                    var na = apre[z] - apre[a];
                    var lvl = 10 * Math.log((pre[z] - pre[a]) / (z - a) / lay.cnt[b8] + 1e-30) / Math.LN10;
                    S2[o] = lvl - (na > 0 ? (gpre[z] - gpre[a]) / na : 0);
                }
            }
            var Qs = new Float32Array(Math.max(1, nb * B)), Rs = new Float32Array(Math.max(1, nb * B)), i, m;
            for (i = 0; i < nb; i++) for (b8 = 0; b8 < B; b8++) {
                m = 0;
                for (j = i * bl; j < Math.min(JA, (i + 1) * bl); j++) { var v = S2[j * B + b8]; if (v > SENTINEL + 1) vals[m++] = v; }
                if (m) { sortVals(m); Qs[i * B + b8] = med(m); } else Qs[i * B + b8] = NaN;
            }
            localMedian(Qs, Rs, nb, Math.round(LOCAL_S / BLOCK_S));
            var DS = 0.05, DN = Math.round(60 / DS) + 1, h3 = new Uint32Array(B * DN), c3 = new Uint32Array(B);
            for (j = 0; j < JA; j++) for (b8 = 0; b8 < B; b8++) {
                var e = S2[j * B + b8];
                if (e <= SENTINEL + 1) continue;
                var rs = interp(Rs, nb, bl, j, b8);
                if (rs !== rs) { S2[j * B + b8] = SENTINEL; continue; }
                S2[j * B + b8] = e - rs;
                h3[b8 * DN + Math.round(Math.min(Math.abs(e - rs), 60) / DS)]++; c3[b8]++;
            }
            for (b8 = 0; b8 < B; b8++) {
                SDs[b8] = c3[b8] >= MIN_ACTIVE_FRAMES ? Math.max(1.0, 1.4826 * histQuantile(h3, b8 * DN, DN, c3[b8], 0.5, 0, DS)) : Infinity;
            }
        }

        /* Шаг C: срез по каждому кадру — ворота открываются от k·σ до (k+1)·σ
           над верхом обычного, срез возвращает к нему, мягкий потолок tanh, вес
           диапазона — и
           «ширина»: срез расходится на соседние полосы по гауссу как МАКСИМУМ со
           спадом, а не как среднее. Усреднение (первая версия) делило одиночный
           пик пополам: при σ 0.8 полосы центр гаусса весит 0.5, и резкая нота с
           выбросом в 4σ прижималась на 1.5–1.9 дБ вместо ~3.5 (стенд, 02.10). */
        var sig = P.sigma, kr = Math.max(1, Math.ceil(3 * sig));
        var kern = new Float32Array(2 * kr + 1);
        for (var i = -kr; i <= kr; i++) kern[i + kr] = Math.exp(-0.5 * (i / sig) * (i / sig));
        // ворота от k·σ до (k+1)·σ над точкой отсчёта; срез возвращает к ней
        var topS = P.sens > 50 ? 1 - (P.sens - 50) / 50 : 1;
        /* Устойчивый путь — только внизу (полный вес до SUS_FULL_HZ, к нулю к
           SUS_ZERO_HZ). Сверху резкую ноту и «с» видит пиковый путь, а
           устойчивый там срабатывал на любую чуть более громкую фразу: 34
           ИИ-трека, 02.10 — он один давал срез на 25 % времени против 16 % у
           пикового. Внизу пиковый слеп (максимумы секунд — удары бочки), и
           бубнение ловит только устойчивый. */
        var SUS_FULL_HZ = 300, SUS_ZERO_HZ = 600, susW = new Float32Array(B);
        for (var bs = 0; bs < B; bs++) {
            var fz = lay.fc[bs];
            susW[bs] = fz <= SUS_FULL_HZ ? 1 : (fz >= SUS_ZERO_HZ ? 0 : 0.5 + 0.5 * Math.cos(Math.PI * Math.log(fz / SUS_FULL_HZ) / Math.log(SUS_ZERO_HZ / SUS_FULL_HZ)));
        }
        function cutOf(D, sd, top) {
            if (!(D > SENTINEL + 1) || !(sd < Infinity)) return 0;
            var x = D - top * sd;
            var gate = (x - P.k * sd) / sd;
            if (gate <= 0) return 0;
            gate = gate >= 1 ? 1 : gate * gate * (3 - 2 * gate);
            return gate * P.depth * Math.tanh(P.ratio * x / P.depth);
        }
        function detectC(j0, j1) {
            for (var j = j0; j < j1; j++) {
                var o = j * B, any = false, b7;
                for (b7 = 0; b7 < B; b7++) {
                    var cut = 0;
                    if (rangeW[b7] > 0) {
                        // пиковый путь: над верхом обычного (секундные максимумы)
                        cut = cutOf(L2[o + b7], SD[b7], 0);
                        // устойчивый путь: над медианой средних + «верх обычного»
                        var cs = susW[b7] ? susW[b7] * cutOf(S2 ? S2[o + b7] : SENTINEL, SDs[b7], topS) : 0;
                        if (cs > cut) cut = cs;
                        cut *= rangeW[b7];
                        if (cut > 0) any = true; else cut = 0;
                    }
                    tmp[b7] = cut;
                }
                if (!any) { for (b7 = 0; b7 < B; b7++) M[o + b7] = 0; continue; }
                for (b7 = 0; b7 < B; b7++) {             // максимум со спадом по гауссу
                    var mxv = 0;
                    for (var d = -kr; d <= kr; d++) {
                        var bb = b7 + d;
                        if (bb < 0 || bb >= B) continue;
                        var vv = tmp[bb] * kern[d + kr];
                        if (vv > mxv) mxv = vv;
                    }
                    M[o + b7] = mxv;
                }
            }
        }

        // Шаг D: время — отпускание вперёд, упреждение назад, лёгкое сглаживание.
        function detectD(b0, b1) {
            var hopT = hopA / sr;
            var aR = Math.exp(-hopT / (P.relMs / 1000)), aA = Math.exp(-hopT / (P.atkMs / 1000));
            for (var b9 = b0; b9 < b1; b9++) {
                var y = 0, j, idx, c2;
                for (j = 0; j < JA; j++) { idx = j * B + b9; c2 = M[idx]; y = c2 > y * aR ? c2 : y * aR; M[idx] = y; }
                y = 0;
                for (j = JA - 1; j >= 0; j--) { idx = j * B + b9; c2 = M[idx]; y = c2 > y * aA ? c2 : y * aA; M[idx] = y; }
                var p1 = M[b9], cur;
                for (j = 0; j < JA; j++) {
                    idx = j * B + b9; cur = M[idx];
                    var nx = j + 1 < JA ? M[idx + B] : cur;
                    M[idx] = 0.25 * p1 + 0.5 * cur + 0.25 * nx;
                    p1 = cur;
                }
            }
        }

        // ── Проход 2: обработка на месте ──
        var acc = [];
        for (c = 0; c < nCh; c++) acc.push(new Float64Array(N));
        var gainBin = new Float64Array(half + 1);
        var delta = null;                                // 10 с вокруг сильнейшего среза
        var rowMax = new Float32Array(Math.max(1, J)), rowMaxA = new Float32Array(Math.max(1, JA));

        function finalizeSpan(p0, p1) {
            for (var ch = 0; ch < nCh; ch++) {
                var dst = chans[ch], ring = acc[ch], ri = ((p0 + off) % N + N) % N;
                for (var pos = p0; pos < p1; pos++) {
                    if (pos >= 0 && pos < len) dst[pos] = ring[ri];
                    ring[ri] = 0;
                    if (++ri === N) ri = 0;
                }
            }
        }
        function pass2(j0, j1) {
            for (var j = j0; j < j1; j++) {
                // срез кадра обработки: кадр анализа j/2 или среднее двух соседних
                var oa = (j >> 1) * B, ob = ((j & 1) && (j >> 1) + 1 < JA) ? oa + B : oa;
                var s = j * hop - off, base = (j * hop) % N, ch, n2, ri;
                var n0 = s < 0 ? -s : 0, nEnd = s + N > len ? len - s : N;
                if (rowMax[j] < UNITY_DB) {
                    // Кадр без среза: тот же вклад, что дал бы FFT-путь с усилением 1.
                    for (ch = 0; ch < nCh; ch++) {
                        var src = chans[ch], ring = acc[ch];
                        ri = (base + n0) % N;
                        for (n2 = n0; n2 < nEnd; n2++) {
                            ring[ri] += src[s + n2] * wprod[n2];
                            if (++ri === N) ri = 0;
                        }
                    }
                } else {
                    for (var k = 0; k <= half; k++) {
                        var bI = lay.bi[k], al = lay.ba[k];
                        var cdb = 0.5 * ((1 - al) * (M[oa + bI] + M[ob + bI]) + al * (M[oa + bI + 1] + M[ob + bI + 1]));
                        gainBin[k] = cdb > 1e-4 ? Math.exp(-cdb * DB2LN) : 1;
                    }
                    for (var p = 0; p < pairs; p++) {
                        loadPair(j, p, win);
                        fft.fwd(re, im);
                        re[0] *= gainBin[0]; im[0] *= gainBin[0];
                        re[half] *= gainBin[half]; im[half] *= gainBin[half];
                        for (k = 1; k < half; k++) {
                            var g = gainBin[k];
                            if (g === 1) continue;
                            re[k] *= g; im[k] *= g; re[N - k] *= g; im[N - k] *= g;
                        }
                        fft.inv(re, im);
                        var ra = acc[2 * p], rb = (2 * p + 1 < nCh) ? acc[2 * p + 1] : null;
                        ri = (base + n0) % N;
                        for (n2 = n0; n2 < nEnd; n2++) {
                            var w = wsyn[n2];
                            ra[ri] += re[n2] * w;
                            if (rb) rb[ri] += im[n2] * w;
                            if (++ri === N) ri = 0;
                        }
                    }
                }
                finalizeSpan(s, s + hop);
            }
            if (j1 === J && J > 0) finalizeSpan((J - 1) * hop - off + hop, (J - 1) * hop - off + N);
        }

        function prepPass2() {
            var any = false, j;
            for (j = 0; j < JA; j++) {
                var mxv = 0, o = j * B;
                for (var b10 = 0; b10 < B; b10++) if (M[o + b10] > mxv) mxv = M[o + b10];
                rowMaxA[j] = mxv;
                if (mxv >= UNITY_DB) any = true;
            }
            for (j = 0; j < J; j++) {
                var ja = j >> 1, jb = ((j & 1) && ja + 1 < JA) ? ja + 1 : ja;
                rowMax[j] = Math.max(rowMaxA[ja], rowMaxA[jb]);
            }
            return any;
        }

        // Окно для «послушать, что убрано»: 10 с с наибольшей суммой срезов.
        function captureDelta(maxCut) {
            if (!(maxCut >= 1)) return;
            var dn = Math.min(len, Math.round(sr * 10));
            if (dn < sr) return;
            var wf = Math.max(1, Math.round(dn / hopA)), sum = 0, best = -1, bestJ = 0, j;
            var tot = new Float32Array(JA);
            for (j = 0; j < JA; j++) { var o = j * B, s2 = 0; for (var b11 = 0; b11 < B; b11++) s2 += M[o + b11]; tot[j] = s2; }
            for (j = 0; j < JA; j++) {
                sum += tot[j]; if (j >= wf) sum -= tot[j - wf];
                if (j >= wf - 1 && sum > best) { best = sum; bestJ = j - wf + 1; }
            }
            if (JA < wf) bestJ = 0;
            var start = clamp(bestJ * hopA - off + (N >> 1), 0, len - dn);
            var orig = [];
            for (var ch = 0; ch < nCh; ch++) orig.push(chans[ch].slice(start, start + dn));
            delta = { start: start, n: dn, orig: orig };
        }

        // Итоги — для честного отчёта на странице и телеметрии.
        function summary() {
            var hopS = hopA / sr, cutF = 0, maxCut = 0, ev = 0, inEv = false, gap = 0;
            var GAP = Math.round(0.15 / hopS);
            var bandTot = new Float64Array(B), bandMax = new Float32Array(B);
            for (var j = 0; j < JA; j++) {
                var o = j * B, mxv = rowMaxA[j];
                if (mxv > maxCut) maxCut = mxv;
                for (var b12 = 0; b12 < B; b12++) { var v = M[o + b12]; bandTot[b12] += v; if (v > bandMax[b12]) bandMax[b12] = v; }
                var hit = active[j] && mxv >= 1;
                if (hit) { cutF++; if (!inEv) { ev++; inEv = true; } gap = 0; }
                else if (inEv && ++gap > GAP) inEv = false;
            }
            var zone = null, topB = -1, topV = 0;
            for (var b13 = 0; b13 < B; b13++) if (bandTot[b13] > topV) { topV = bandTot[b13]; topB = b13; }
            if (topB >= 0 && maxCut >= 1) {
                var zl = topB, zh = topB;
                while (zl > 0 && bandTot[zl - 1] >= topV * 0.5) zl--;
                while (zh < B - 1 && bandTot[zh + 1] >= topV * 0.5) zh++;
                var zMax = 0; for (b13 = zl; b13 <= zh; b13++) if (bandMax[b13] > zMax) zMax = bandMax[b13];
                var hb = Math.pow(2, 1 / (2 * BPO));
                zone = { lo: lay.fc[zl] / hb, hi: lay.fc[zh] * hb, maxDb: zMax };
            }
            // Карта для страницы: ≤ 600 столбцов × B строк, максимум по столбцу
            // (короткий «с» не должен исчезнуть при прореживании), 0.05 дБ на шаг.
            var W = Math.max(1, Math.min(JA, 600)), data = new Uint8Array(W * B);
            for (var col = 0; col < W; col++) {
                var f0 = Math.floor(col * JA / W), f1 = Math.max(f0 + 1, Math.floor((col + 1) * JA / W));
                for (var b14 = 0; b14 < B; b14++) {
                    var mv = 0;
                    for (var jj = f0; jj < f1 && jj < JA; jj++) { var vv = M[jj * B + b14]; if (vv > mv) mv = vv; }
                    data[col * B + b14] = Math.min(255, Math.round(mv * 20));
                }
            }
            return {
                cutFrames: cutF, activeFrames: nActive,
                cutShare: nActive ? cutF / nActive : 0,
                events: ev, maxCutDb: maxCut, zone: zone,
                map: { cols: W, bands: B, data: data, bandHz: Array.prototype.slice.call(lay.fc), duration: len / sr }
            };
        }

        return {
            J: J, JA: JA, B: B, N: N, hop: hop, hopA: hopA,
            enough: function () { return JA > 0 && nActive >= MIN_ACTIVE_FRAMES; },
            pass1: pass1, prepActive: prepActive, detectA: detectA,
            stats: function () { statLevels(false); statOffsets(); statLevels(true); statOffsets(); localize(); },
            detectC: detectC, detectD: detectD, prepPass2: prepPass2, pass2: pass2,
            captureDelta: captureDelta, summary: summary,
            finishDelta: function () {
                if (!delta) return null;
                var out = [];
                for (var ch = 0; ch < nCh; ch++) {
                    var o2 = delta.orig[ch], now = chans[ch], d = new Float32Array(delta.n);
                    for (var i2 = 0; i2 < delta.n; i2++) d[i2] = o2[i2] - now[delta.start + i2];
                    out.push(d);
                }
                return { start: delta.start / sr, sampleRate: sr, channels: out };
            },
            rowMaxCut: function () { var m2 = 0; for (var j = 0; j < JA; j++) if (rowMaxA[j] > m2) m2 = rowMaxA[j]; return m2; },
            // для стенда: внутренние карты
            _M: M, _L2: L2, _S2: function () { return S2; }, _SDs: SDs, _fc: lay.fc, _active: active, _Lt: Lt, _SD: SD, _G: G
        };
    }

    function noRun(reason) { return { ran: false, reason: reason }; }

    // Синхронный драйвер: node-тесты и короткие буферы.
    function process(buffer, params) {
        var P = (params && params.k != null) ? params : resolve(params);
        if (!P) return noRun('off');
        var t0 = Date.now();
        var job = createJob(buffer, P);
        if (job.J < 1) return noRun('empty');
        job.pass1(0, job.JA);
        job.prepActive();
        if (!job.enough()) return noRun('short');
        job.detectA(0, job.JA); job.stats(); job.detectC(0, job.JA); job.detectD(0, job.B);
        var any = job.prepPass2(), res;
        if (any) {
            job.captureDelta(job.rowMaxCut());
            job.pass2(0, job.J);
            res = job.summary();
            res.delta = job.finishDelta();
        } else {
            res = job.summary();
            res.delta = null;
        }
        res.ran = true; res.touched = any; res.params = P; res.ms = Date.now() - t0;
        return res;
    }

    // Нарезанный драйвер: ломти ≈ 24 мс с настоящей макрозадачей между ними —
    // .then — микрозадача и поток не отпускает (урок mastering-sliced-passes).
    // __yieldTick(0) — канал и в скрытой вкладке (docs/why/primitives/hidden-tab-yield.md).
    function yielder() {
        if (typeof root.__yieldTick === 'function') return root.__yieldTick(0);
        return new Promise(function (r) { setTimeout(r, 0); });
    }
    function now() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

    async function processAsync(buffer, params, opts) {
        opts = opts || {};
        var P = (params && params.k != null) ? params : resolve(params);
        if (!P) return noRun('off');
        var signal = opts.signal || null, onProg = opts.onProgress || null;
        var SLICE = opts.sliceMs || 24, t0 = now();
        function check() { if (signal && signal.aborted) throw abortErr(); }
        function prog(p) { if (onProg) { try { onProg(p); } catch (_) {} } }
        async function run(fn, total, p0, p1, unit) {
            var i = 0;
            while (i < total) {
                var ts = now();
                while (i < total && now() - ts < SLICE) {
                    var e = Math.min(total, i + unit);
                    fn(i, e); i = e;
                }
                prog(p0 + (p1 - p0) * (i / total));
                await yielder(); check();
            }
        }
        check();
        var job = createJob(buffer, P);
        if (job.J < 1) return noRun('empty');
        await run(job.pass1, job.JA, 0, 0.45, 8);
        job.prepActive();
        if (!job.enough()) { prog(1); return noRun('short'); }
        await run(job.detectA, job.JA, 0.45, 0.5, 256);
        job.stats();
        await yielder(); check();
        await run(job.detectC, job.JA, 0.5, 0.54, 256);
        await run(job.detectD, job.B, 0.54, 0.56, 4);
        var any = job.prepPass2(), res;
        if (any) {
            job.captureDelta(job.rowMaxCut());
            await run(job.pass2, job.J, 0.56, 1, 8);
            res = job.summary();
            res.delta = job.finishDelta();
        } else {
            res = job.summary();
            res.delta = null;
        }
        prog(1);
        res.ran = true; res.touched = any; res.params = P; res.ms = Math.round(now() - t0);
        return res;
    }

    var API = {
        AUTO: AUTO, LIMITS: LIMITS, FOCUS: FOCUS,
        resolve: resolve, describe: describe,
        process: process, processAsync: processAsync,
        // для стенда: то, чем режет обработка
        _bandLayout: bandLayout, _frameSize: frameSize, _createJob: createJob, _makeFFT: makeFFT
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (typeof window !== 'undefined') window.MasteringDynamicEq = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
